import {Component, OnInit} from '@angular/core';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {formatDate} from '@angular/common';
import {saveAs} from 'file-saver';
import {RebillingService} from '../../../services/rebilling.service';
import {RebillingFilters, RebillingLine, RebillingNote, RebillingSheet, RebillingSheetCell, RebillingSheets} from '../../../models/rebilling';
import {UtilsService} from '../../../../appSharedModule/services/utils.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-rebilling',
  templateUrl: './rebilling.component.html',
  styleUrls: ['./rebilling.component.scss']
})
export class RebillingComponent implements OnInit {

  readonly exchanges: SelectItem[] = ['EUREX', 'ICE', 'LME', 'LSE - TQ'].map(value => ({label: value, value}));
  readonly statuses: SelectItem[] = ['Draft', 'OB Final', 'Ready to Bill', 'Billed', 'Hold', 'Review Needed'].map(value => ({label: value, value}));
  readonly months: SelectItem[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((label, index) => ({label, value: index + 1}));
  readonly quarters: SelectItem[] = [1, 2, 3, 4].map(value => ({label: 'Q' + value, value}));
  readonly groupByOptions: SelectItem[] = [
    {label: 'Contracting entity', value: 'entity'},
    {label: 'Account', value: 'account'},
    {label: 'Month', value: 'month'},
    {label: 'Quarter', value: 'quarter'},
    {label: 'Year', value: 'year'}
  ];

  readonly periodColumns = Array.from({length: 13});
  readonly currencySymbols: Record<string, string> = {USD: '$', EUR: '€', GBP: '£'};
  readonly incompleteTitle = 'Includes missing data';
  readonly selfReportedTitle = 'Self reported';
  readonly noDadTitle = 'No DAD form';

  year = new Date().getFullYear();
  filters: RebillingFilters = {year: this.year};
  groupBy: string = null;
  lines: RebillingLine[] = [];
  sheets: RebillingSheets = null;
  activeView = 0;
  selectedLines: RebillingLine[] = [];
  bulkStatus: string = null;
  companies: SelectItem[] = [];
  accounts: SelectItem[] = [];
  products: SelectItem[] = [];
  entities: SelectItem[] = [];
  notes: RebillingNote[] = [];
  newNote = {billingAccount: null as number, exchange: null as string, noteDate: new Date(), text: ''};
  generateResult: string = null;
  flashedLineIds = new Set<number>();
  errorMessage: string = null;
  editingNoteId: number = null;
  successMessage: string = null;
  private successTimer: ReturnType<typeof setTimeout>;
  private linesSub: Subscription;
  private optionsSub: Subscription;
  private sheetsSub: Subscription;
  private notesSub: Subscription;

  constructor(private rebillingService: RebillingService, private utilsService: UtilsService,
              private confirmationService: ConfirmationService) {
  }

  ngOnInit() {
    this.loadLines(true);
    this.loadNotes();
  }

  changeYear() {
    this.cancelEdit();
    this.filters = {year: this.year};
    this.loadLines(true);
    this.loadNotes();
  }

  resetFilters() {
    this.filters = {year: this.year};
    this.loadLines();
    this.loadNotes();
  }

  confirmGenerate() {
    this.confirmationService.confirm({
      key: 'generateRebillingCD',
      header: `Generate ${this.year}`,
      message: `This will rebuild the ${this.year} rebilling lines from the DADs. ` +
        'Draft lines are recalculated or removed; lines past Draft are not changed but get flagged if their source changed.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Generate',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-outlined p-button-danger',
      defaultFocus: 'reject',
      accept: () => this.generate()
    });
  }

  generate() {
    this.utilsService.lockUi();
    this.rebillingService.generate(this.year).subscribe({
      next: result => {
        this.generateResult = `${result.inserted} new, ${result.updated} updated, ${result.deleted} deleted, ${result.flagged} flagged`;
        this.utilsService.unlockUi();
        this.loadLines(true);
      },
      error: error => this.handleError(error)
    });
  }

  loadLines(refreshOptions = false) {
    this.errorMessage = null;
    this.loadSheets();
    if (refreshOptions) {
      this.loadOptions();
    }
    this.linesSub?.unsubscribe();
    this.linesSub = this.rebillingService.getLines({...this.filters, year: this.year}).subscribe({
      next: lines => {
        this.lines = lines;
        this.selectedLines = [];
      },
      error: error => this.handleError(error)
    });
  }

  loadOptions() {
    this.optionsSub?.unsubscribe();
    this.optionsSub = this.rebillingService.getLines({year: this.year}).subscribe({
      next: lines => {
        this.companies = this.uniqueOptions(lines.filter(line => line.companyId).map(line => ({label: line.company, value: line.companyId})));
        this.accounts = this.uniqueOptions(lines.filter(line => line.billingAccount).map(line => ({label: line.billingAccount.name, value: line.billingAccount.id})));
        this.products = this.uniqueOptions(lines.filter(line => line.dadProductId).map(line => ({label: line.internalProductCode, value: line.dadProductId})));
        this.entities = this.uniqueOptions(lines.filter(line => line.contractingEntity).map(line => ({label: line.contractingEntity.name, value: line.contractingEntity.id})));
      },
      error: error => this.handleError(error)
    });
  }

  loadSheets() {
    this.sheetsSub?.unsubscribe();
    this.sheetsSub = this.rebillingService.getSheets({...this.filters, year: this.year}).subscribe({
      next: sheets => this.sheets = sheets,
      error: error => this.handleError(error)
    });
  }

  clearPeriod(kind: 'month' | 'quarter') {
    if (kind === 'month') {
      this.filters.quarter = undefined;
    } else {
      this.filters.month = undefined;
    }
    this.loadLines();
  }

  applyStatus() {
    if (!this.bulkStatus || this.selectedLines.length === 0) {
      return;
    }
    const ids = this.selectedLines.map(line => line.id);
    this.rebillingService.updateStatus(ids, this.bulkStatus).subscribe({
      next: () => {
        this.bulkStatus = null;
        this.flashedLineIds = new Set(ids);
        this.loadLines();
        setTimeout(() => this.flashedLineIds = new Set<number>(), 2500);
      },
      error: error => this.handleError(error)
    });
  }

  get activeFilterCount(): number {
    return Object.entries(this.filters).filter(([key, value]) => key !== 'year' && value !== undefined && value !== null && value !== false).length;
  }

  statusClass(status: string): string {
    return 'status-pill--' + status.toLowerCase().replace(/ /g, '-');
  }

  exportWorkbook() {
    this.utilsService.lockUi();
    this.rebillingService.export({...this.filters, year: this.year}, this.groupBy).subscribe({
      next: data => {
        saveAs(new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}), formatDate(new Date(), 'yyyy-MM-dd', 'en-US') + '_Rebilling_' + this.year + '.xlsx');
        this.utilsService.unlockUi();
      },
      error: error => this.handleError(error)
    });
  }

  loadNotes() {
    this.notesSub?.unsubscribe();
    this.notesSub = this.rebillingService.getNotes(this.year, this.filters.exchange).subscribe({
      next: notes => this.notes = notes,
      error: error => this.handleError(error)
    });
  }

  get canSaveNote(): boolean {
    return !!this.newNote.billingAccount && !!this.newNote.exchange && !!this.newNote.text.trim();
  }

  saveNote() {
    if (!this.canSaveNote) {
      return;
    }
    const note = {
      billingAccount: this.newNote.billingAccount,
      exchange: this.newNote.exchange,
      noteDate: formatDate(this.newNote.noteDate, 'yyyy-MM-dd', 'en-US'),
      text: this.newNote.text
    };
    const request = this.editingNoteId
      ? this.rebillingService.updateNote(this.editingNoteId, note)
      : this.rebillingService.createNote({...note, year: this.year});
    const updated = !!this.editingNoteId;
    request.subscribe({
      next: () => {
        this.cancelEdit();
        this.loadNotes();
        this.showSuccess(updated ? 'Note updated.' : 'Note added.');
      },
      error: error => this.handleError(error)
    });
  }

  editNote(note: RebillingNote) {
    const [year, month, day] = note.noteDate.split('-').map(Number);
    this.editingNoteId = note.id;
    this.newNote = {billingAccount: note.billingAccount.id, exchange: note.exchange, noteDate: new Date(year, month - 1, day), text: note.text};
  }

  cancelEdit() {
    this.editingNoteId = null;
    this.newNote = {billingAccount: this.newNote.billingAccount, exchange: this.newNote.exchange, noteDate: new Date(), text: ''};
  }

  confirmDeleteNote(note: RebillingNote) {
    this.confirmationService.confirm({
      key: 'deleteNoteCD',
      header: 'Delete note',
      message: `Delete the ${note.exchange} note for ${note.billingAccount.name} dated ${formatDate(note.noteDate, 'dd/MM/yyyy', 'en-US')}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-outlined p-button-danger',
      defaultFocus: 'reject',
      accept: () => this.deleteNote(note)
    });
  }

  deleteNote(note: RebillingNote) {
    this.rebillingService.deleteNote(note.id).subscribe({
      next: () => {
        if (this.editingNoteId === note.id) {
          this.cancelEdit();
        }
        this.loadNotes();
        this.showSuccess('Note deleted.');
      },
      error: error => this.handleError(error)
    });
  }

  showSuccess(message: string) {
    this.successMessage = message;
    clearTimeout(this.successTimer);
    this.successTimer = setTimeout(() => this.successMessage = null, 4000);
  }

  cellTitle(cell: RebillingSheetCell): string | null {
    if (cell.incomplete) {
      return this.incompleteTitle;
    }
    if (cell.selfReported) {
      return this.selfReportedTitle;
    }
    return cell.noDad ? this.noDadTitle : null;
  }

  formatFee(fee: number | null, currency: string | null): string {
    if (fee === null) {
      return '';
    }
    const amount = new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(fee);
    const symbol = this.currencySymbols[currency];
    if (symbol) {
      return symbol + amount;
    }
    return currency ? amount + ' ' + currency : amount;
  }

  trackByExchange(index: number, sheet: RebillingSheet): string {
    return sheet.exchange;
  }

  hasHighException(line: RebillingLine): boolean {
    return line.exceptions.some(exception => exception.severity === 'High');
  }

  private uniqueOptions(options: SelectItem[]): SelectItem[] {
    const seen = new Set();
    return options.filter(option => !seen.has(option.value) && seen.add(option.value)).sort((a, b) => a.label.localeCompare(b.label));
  }

  private handleError(error: any) {
    this.utilsService.unlockUi();
    this.errorMessage = error?.error?.message ?? 'Request failed.';
  }
}
